import { createMedia } from '@artsy/fresnel';
import { useInject } from 'dependable-react';
import { css, injectGlobal } from 'emotion';
import { observer, useComputed, useObservable } from 'mobx-react-lite';
import * as React from 'react';
import { useAsync } from 'react-use';

import { Book } from '../components/Book';
import { Charbuilder } from '../components/Charbuilder';
import { ConfirmModal } from '../components/ConfirmModal';
import { FlowButtons } from '../components/FlowButtons';
import { Loader } from '../components/Loader';
import { MagicBuilder } from '../components/MagicBuilder';
import { MoreProducts } from '../components/MoreProducts';
import { TabContainer } from '../components/TabContainer';
import { ITab } from '../components/TabContainerDesktop';
import { createToast, ToastContainer } from '../components/ToastContainer';
import { BREAKPOINT_LARGE, BREAKPOINT_SMALL } from '../consts/breakpoints';
import { MoreMagicType } from '../enums/MoreMagicType';
import { Page } from '../enums/Page';
import { APP_DATA_TOKEN, BOOK_SERVICE_TOKEN, I18N_TOKEN } from '../services/InjectTokens';
import { Avatar, Field, Magic, Upload } from '../state/models';
import { Element } from '../state/models/Element';
import { translateAlphabet } from '../utils/alphabetTranslator';
import { pushGtmData } from '../utils/gtm';
import { validateFields } from '../utils/validateFields';
import { IGNORE_PARAMS } from '../consts/translationIgnoreParams';
import { runInAction } from 'mobx';

interface IState {
  lastEditedTab: string | null;
  selectedTab: number | null | string;
  shouldAddMagicTabs: boolean;
  showMoreProducts: boolean;
  additionalStep: string;
  showConfirm: boolean;
  magicPersons: Array<Element> | null;
  currentlyVisiblePage: number | null;
}

const toastContainer = css`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  z-index: 510;

  display: flex;
  justify-content: center;
  align-items: center;

  @media (min-width: ${BREAKPOINT_SMALL}) {
    dislay: none;
  }
`;

const container = (isSelectedTab: boolean) => css`
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: ${BREAKPOINT_SMALL}) {
    ${isSelectedTab && 'position: fixed;'}
    ${isSelectedTab && 'z-index: 501;'}// bigger than hamburger icon on rails app
    flex-direction: column-reverse;
  }
`;

const buttonsContainer = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  background: #fff;
  order: -2;

  padding: 40px 0;

  @media (min-width: ${BREAKPOINT_SMALL}) {
    flex-direction: row;
    order: 0;
    padding: 20px;
  }

  @media (min-width: ${BREAKPOINT_LARGE}) {
    padding: 20px 160px;
    justify-content: flex-end;
  }

  > div:not(:last-child) {
    margin-bottom: 17px;

    @media (min-width: ${BREAKPOINT_SMALL}) {
      margin-bottom: 0;
    }
  }
`;

interface IAppProps {
  bookLoader: Promise<void>;
  initialPage: Page;
  skipProducts: boolean;
}

export const { MediaContextProvider, Media } = createMedia({
  breakpoints: {
    sm: 0,
    md: 768,
    lg: 992,
    xl: 1192,
  },
});

export const App: React.FC<IAppProps> = observer<IAppProps>((props) => {
  const appState = useInject(APP_DATA_TOKEN);
  const i18n = useInject(I18N_TOKEN);
  const bookService = useInject(BOOK_SERVICE_TOKEN);
  const allAvatarsCompleted = useComputed<boolean>(validateFields);
  const magicCompleted = useComputed<Array<Magic>>(() => appState.moreMagic.filter(({ isCompleted }) => isCompleted));

  const state = useObservable<IState>({
    additionalStep: '',
    currentlyVisiblePage: null,
    lastEditedTab: null,
    magicPersons: null,
    selectedTab: null,
    shouldAddMagicTabs: true,
    showConfirm: false,
    showMoreProducts: false,
  });

  const tabs = useComputed<Array<ITab>>(() => {
    const moreMagic = appState.moreMagic.map(({ title, name, isCompleted, type }) => ({
      id: name,
      isCompleted,
      isSelected: state.selectedTab === name,
      name,
      title,
      type,
      isMoreMagic: true,
    }));

    const avatars = appState.avatars.map(({ id, name, title, isCompleted }) => ({
      id,
      isCompleted,
      isSelected: state.selectedTab === id,
      name,
      title,
      isMoreMagic: false,
    }));

    if (state.shouldAddMagicTabs) {
      return [...avatars, ...moreMagic];
    }

    return avatars;
  });

  const allTabsCompleted = React.useMemo(
    () => tabs.filter(({ isMoreMagic }) => !isMoreMagic).every((tab) => tab.isCompleted),
    [tabs],
  );

  const allMagicCompleted = React.useMemo(
    () => tabs.filter(({ isMoreMagic }) => isMoreMagic).every((tab) => tab.isCompleted),
    [tabs],
  );

  function resetCurrentPage() {
    if (state.currentlyVisiblePage === null) {
      return;
    }
    state.currentlyVisiblePage = null;
  }

  function onSelectedTab(id: number | string) {
    if (state.selectedTab === id) {
      state.selectedTab = null;
    } else {
      state.selectedTab = id;
    }
  }

  function onClose() {
    state.selectedTab = null;
  }

  function translateOtherVariables(avatar: Avatar) {
    /**Update avatars */
    appState.avatars.map((stateAvatar) => {
      if (stateAvatar.id !== avatar.id && stateAvatar?.params) {
        Object.entries(stateAvatar.params).map(([key, value]) => {
          if (value && !IGNORE_PARAMS.includes(key)) {
            stateAvatar.params[key] = translateAlphabet(value);
          }
        });
      }
    });

    /**Update more magic (dedication and friends) */
    appState.moreMagic.map((magic) => {
      if (magic.params) {
        Object.entries(magic.params).map(([key, value]) => {
          magic.params[key] = translateAlphabet(value);
        });
      }
    });
  }

  function onCharSave(avatar: Avatar, data: Record<string, string>, mainCharacter?: boolean) {
    if (mainCharacter) {
      translateOtherVariables(avatar);
    }

    //TODO: Move this to form
    Object.entries(data).map(([key, value]) => {
      if (!IGNORE_PARAMS.includes(key)) {
        data[key] = translateAlphabet(value);
      }
    });
    avatar.params = data;

    state.lastEditedTab = avatar.uid;
    //state.currentlyVisiblePage = avatar.firstAppearedOn;
    pushGtmData({
      event: 'personalizacija',
      korak: { items: [{ step: `step_preview_${avatar.title}` }] },
    });
    goToNextTabInRow();
    // onClose();
  }

  function onMoreMagicRemovePhoto(imageToRemove: Upload) {
    appState.removeOne(imageToRemove);
  }

  function onMoreMagicSave(magic: Magic) {
    return (params: Record<string, string>, upload?: Upload) => {
      Object.entries(params).map(([key, value]) => {
        params[key] = translateAlphabet(value);
      });

      magic.params = params;
      magic.path = upload?.path || null;
      magic.isSaved = true;
      state.lastEditedTab = magic.type;

      if (magic.type === MoreMagicType.CHILD_IMAGE) {
        magic.path = appState.childImage?.path || null;
      }

      pushGtmData({
        event: 'personalizacija',
        korak: { items: [{ step: `step_preview_${magic.title}` }] },
      });

      if (upload) {
        const oldChildImage = appState.findAll(Upload).find(({ type }) => type === MoreMagicType.CHILD_IMAGE);
        const oldDedicationImage = appState.findAll(Upload).find(({ type }) => type === MoreMagicType.DEDICATION);
        upload.type = magic.type;
        magic.path = upload?.path;

        if (magic.type === MoreMagicType.CHILD_IMAGE && oldChildImage) {
          appState.removeOne(oldChildImage);
        }
        if (magic.type === MoreMagicType.DEDICATION && oldDedicationImage) {
          appState.removeOne(oldDedicationImage);
        }

        appState.add(upload);
      }

      //state.currentlyVisiblePage = magic.firstAppearedOn;

      // onClose();
      goToNextTabInRow();
    };
  }

  function addMoreMagic() {
    state.shouldAddMagicTabs = true;
    state.selectedTab = appState.moreMagic[0].name;
  }

  function onContinue(additionalStep?: Page) {
    const { extraProducts, wraps } = appState;

    if ((!extraProducts.length && !wraps.length) || props.skipProducts) {
      state.showConfirm = true;

      return;
    }

    state.showMoreProducts = true;

    if (additionalStep) {
      state.additionalStep = additionalStep;

      return;
    }

    if (!state.additionalStep && !wraps.length && extraProducts.length) {
      state.additionalStep = Page.MORE_PRODUCTS;

      return;
    }

    if (!state.additionalStep && wraps.length) {
      state.additionalStep = Page.WRAP;

      return;
    }

    if (state.additionalStep === Page.WRAP && extraProducts.length) {
      state.additionalStep = Page.MORE_PRODUCTS;

      return;
    }

    if (state.additionalStep === Page.MORE_PRODUCTS || state.additionalStep === Page.WRAP) {
      state.additionalStep = '';
      state.showMoreProducts = false;
      state.showConfirm = true;

      return;
    }
  }

  function onNextTab() {
    const firstUncompletedTab = tabs.find((tab) => !tab.isCompleted);
    if (firstUncompletedTab) {
      onSelectedTab(firstUncompletedTab.id);
    }

    return firstUncompletedTab;
  }

  function goToNextTabInRow() {
    const currentTab = tabs.find((tab) => tab.id === state.selectedTab);

    if (!currentTab) return;

    const nextTab = tabs[tabs.indexOf(currentTab) + 1];

    if (!nextTab) {
      onNextTab() || onClose();
    } else {
      state.selectedTab = nextTab.id || nextTab.name;
    }
  }

  function onModalClose() {
    state.showConfirm = false;
  }

  function onMagicError() {
    state.selectedTab = null;
    createToast(i18n.t('upload_failed'));
  }

  function onBack() {
    if (state.additionalStep === Page.MORE_PRODUCTS && appState.wraps.length) {
      state.additionalStep = Page.WRAP;

      return;
    }

    if (
      state.additionalStep === Page.WRAP ||
      !appState.extraProducts.length ||
      (state.additionalStep === Page.MORE_PRODUCTS && !appState.wraps.length)
    ) {
      state.showMoreProducts = false;
      state.additionalStep = '';
    }
  }

  async function onConfirm() {
    try {
      const { url } = await bookService.submit();
      window.location.href = url;
    } catch (e) {
      onModalClose();
      createToast(i18n.t('submit_failed'));
    }
  }

  function onChangeLang(lang: string) {
    appState.currentLanguage = lang;
  }

  const { loading: isLoading } = useAsync(async () => props.bookLoader);

  /* React.useEffect(() => {
    let langOverride: string;

    const params = new URLSearchParams(window.location.search);
    if (params.get('lang')) {
      langOverride = params.get('lang')!;

      runInAction(() => {
        appState.currentLanguage = langOverride;
      });
    }
  }, []); */

  React.useEffect(() => {
    const tab = state.lastEditedTab;
    if (tab) {
      createToast(i18n.t(`${tab}_completed`));
      state.lastEditedTab = null;
    }
  }, [state.selectedTab]);

  React.useEffect(() => {
    if (isLoading || !appState.avatars.length) {
      return;
    }

    state.selectedTab = tabs[0].id;

    if (props.initialPage === Page.MORE_PRODUCTS || props.initialPage === Page.WRAP) {
      onContinue(props.initialPage);
    }
  }, [props.initialPage, isLoading, appState.avatars]);

  React.useEffect(() => {
    if (magicCompleted.length && !state.shouldAddMagicTabs) {
      state.shouldAddMagicTabs = true;
    }
  }, [magicCompleted]);

  React.useEffect(() => {
    if (!isLoading) {
      injectGlobal(`
        ${appState.fonts
          .map((font) => {
            return `
              @font-face {
                font-family: "${font.name}";
                src: ${font.fileSources.join(', ').replace(/, ([^,]*)$/, '')};
                font-style: ${font.style || 'normal'};
                font-weight: ${font.weight || 'normal'};
              }
          `;
          })
          .join('')}
      `);
    }
  }, [isLoading]);

  return (
    <MediaContextProvider>
      <Loader isLoading={isLoading} />
      {!isLoading && appState.avatars && appState.banner && (
        <React.Fragment>
          <div className={container(Boolean(state.selectedTab || state.showConfirm))}>
            {!state.showMoreProducts && (
              <React.Fragment>
                <TabContainer tabs={tabs} onSelectedTab={onSelectedTab} />
                <Book
                  avatars={appState.avatars}
                  currentPage={state.currentlyVisiblePage}
                  magicPersons={appState.iconedMagic}
                  resetCurrentPage={resetCurrentPage}
                />
              </React.Fragment>
            )}
            {state.showMoreProducts && (
              <MoreProducts moreProducts={appState.extraProducts} wraps={appState.wraps} type={state.additionalStep} />
            )}
            <div className={buttonsContainer}>
              <FlowButtons
                magic={{
                  // show: !state.shouldAddMagicTabs && !state.showMoreProducts && allAvatarsCompleted,
                  show: false,
                  onClick: addMoreMagic,
                }}
                back={{
                  show: state.showMoreProducts,
                  onClick: onBack,
                }}
                nextAvatar={{
                  // show: !allTabsCompleted,
                  show: false,
                  disabled: Boolean(state.selectedTab),
                  onClick: onNextTab,
                }}
                nextMoreMagic={{
                  show: !state.showMoreProducts, // !allMagicCompleted && !state.showMoreProducts,
                  disabled: Boolean(state.selectedTab),
                  onClick:
                    !allMagicCompleted && !state.showMoreProducts
                      ? onNextTab
                      : allTabsCompleted && !state.showMoreProducts
                      ? onContinue
                      : onNextTab,
                }}
                addToCart={{
                  show: allTabsCompleted && !state.showMoreProducts && false,
                  disabled: Boolean(state.selectedTab),
                  onClick: onContinue,
                }}
                continueProducts={{
                  show: state.showMoreProducts,
                  onClick: onContinue,
                }}
              />
            </div>
            {appState.avatars.map((avatar) => (
              <Charbuilder
                bookId={appState.bookId}
                key={avatar.id}
                avatar={avatar}
                onSave={onCharSave}
                onClose={onClose}
                onChangeLang={onChangeLang}
                currentLang={appState.currentLanguage}
                isVisible={avatar.id === state.selectedTab}
              />
            ))}
            {appState.moreMagic.map((magic) => (
              <MagicBuilder
                key={magic.type}
                elements={magic.elements}
                params={magic.params}
                onClose={onClose}
                onError={onMagicError}
                onRemovePhoto={onMoreMagicRemovePhoto}
                images={{
                  childImage: appState.childImage,
                  dedicationImage: appState.dedicationImage,
                }}
                onSave={onMoreMagicSave(magic)}
                isVisible={state.selectedTab === magic.name}
                type={magic.type}
                title={magic.title}
              />
            ))}
            <ToastContainer className={toastContainer} />
            <ConfirmModal isVisible={state.showConfirm} onClose={onModalClose} onConfirm={onConfirm} />
          </div>
        </React.Fragment>
      )}
    </MediaContextProvider>
  );
});
